import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';

import {
  BooleanTypeSelectItemsProviderService,
  DrawerContentComponent,
  DrawerTitleComponent,
  Icon,
  PrimengDrawerDirective,
  TableBuilderFactory,
  TableCollectionAction,
  TableCollectionActionBuilderFactory,
  TableCollectionActionsComponent,
  TableComponent,
  TableConfig,
  TableDataPayload,
  TableLazyLoad,
  TableRowsSelection,
  TableService,
  ToastService,
} from '@web/shared/ui';
import { AdminOrderCollectionItem } from '../../models/admin-order-collection-item';
import { AdminOrderCollectionFilter } from '../../models/admin-order-collection-filter';
import { AdminOrderListItemAction } from '../../enums/admin-order-list-item-action';
import { Route, RouteParam } from '@web/common';
import { Observable, switchMap, takeWhile, tap, timer } from 'rxjs';
import { AdminOrderDataSourceService } from '../../services';
import { AdminOrderListCustomCellTemplateComponent } from '../../components/admin-order-list-custom-cell-template/admin-order-list-custom-cell-template.component';
import { AdminOrderPartialRefundFormComponent } from '../admin-order-partial-refund-form/admin-order-partial-refund-form.component';
import { OrderStatusProviderService, ShipmentStatusProviderService } from '@web/shared/module/shop';
import { Guid, isNil, once } from '@web/utils';
import { AdminOrderCollectionAction } from '../../enums/admin-order-collection-action';
import { AdminDictionaryCountrySelectItemsProviderService } from '@web/shared/module/dictionary';
import {
  AdminOrderStatsProvider,
  AdminShippingMethodSelectItemsProviderService,
} from '../../providers';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Drawer } from 'primeng/drawer';
import { AdminBackgroundTaskDataSourceService } from '@web/features/system';

@Component({
  selector: 'app-admin-order-list',
  imports: [
    TableComponent,
    AdminOrderListCustomCellTemplateComponent,
    TableCollectionActionsComponent,
    Drawer,
    DrawerTitleComponent,
    DrawerContentComponent,
    PrimengDrawerDirective,
    AdminOrderPartialRefundFormComponent,
  ],
  templateUrl: './admin-order-list.component.html',
  styleUrl: './admin-order-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdminOrderListComponent implements OnInit {
  /* prettier-ignore */
  private readonly builder = inject(TableBuilderFactory<AdminOrderCollectionItem, AdminOrderCollectionFilter, AdminOrderListItemAction, AdminOrderCollectionAction>).create();
  /* prettier-ignore */
  protected config!: TableConfig<AdminOrderCollectionItem, AdminOrderCollectionFilter, AdminOrderListItemAction, AdminOrderCollectionAction>;
  /* prettier-ignore */
  protected customCollectionActions = signal<Array<TableCollectionAction<AdminOrderCollectionItem, AdminOrderCollectionFilter, AdminOrderCollectionAction>>>([]);
  protected readonly partialRefundDrawerVisibility = signal(false);
  protected readonly partialRefundOrderId = signal<Guid | null>(null);

  /* prettier-ignore */
  private readonly orderStatusProvider = inject(OrderStatusProviderService);
  private readonly shipmentStatusProvider = inject(ShipmentStatusProviderService);
  private readonly orderDataSource = inject(AdminOrderDataSourceService);
  private readonly backgroundTaskDataSource = inject(AdminBackgroundTaskDataSourceService);
  private readonly toast = inject(ToastService);
  private readonly adminOrderStats = inject(AdminOrderStatsProvider);
  /* prettier-ignore */
  private readonly shippingMethodSelectItemsProvider = inject(AdminShippingMethodSelectItemsProviderService);
  private readonly waitingForMarkAsCompleteTaskReady = signal<boolean>(false);
  private readonly destroyRef = inject(DestroyRef);
  private readonly table = inject(TableService);
  /* prettier-ignore */
  private readonly booleanTypeSelectItemsProvider = inject(BooleanTypeSelectItemsProviderService);
  /* prettier-ignore */
  private readonly tableCollectionActionBuilder = inject(TableCollectionActionBuilderFactory<AdminOrderCollectionItem, AdminOrderCollectionFilter, AdminOrderCollectionAction>).create();
  /* prettier-ignore */
  private readonly dictionaryCountrySelectItemsProvider = inject(AdminDictionaryCountrySelectItemsProviderService);

  private readonly undefinedProgress = -1;
  private readonly backgroundTaskProgress = signal<number>(this.undefinedProgress);

  public ngOnInit(): void {
    this.buildTable();
    this.refreshPaidOrderCounts();
    this.configureTableCollectionActions();
  }

  /* prettier-ignore */
  private buildTable(): void {
    this.config = this.builder
      .dataKey('id')
      .itemActions((builder) => [
        builder
          .initRouterLink(AdminOrderListItemAction.Preview)
          .visibility((item) => item.canBeViewed)
          .icon(Icon.Eye)
          .tooltip('Podgląd')
          .routerLink(
            (provider) => (item) =>
              provider.routerLink(Route.OrderDetails, {
                [RouteParam.Guid]: item.id,
              }),
          )
          .build(),




        builder.initMore(AdminOrderListItemAction.More)
          .visibility((item) => this.shouldVisibleMore(item))
          .children((builder) => [
            builder
              .initCommand(AdminOrderListItemAction.MarkAsPaid)
              .icon(Icon.CashCoin)
              .tooltip('Oznacz jako opłacone')
              .visibility((item) => item.canBeMarkAsPaid)
              .command((model) => this.confirmAndMarkAsPaid(model))
              .build(),

            builder
              .initCommand(AdminOrderListItemAction.PartialRefund)
              .visibility((item) => item.canBeRefunded)
              .icon(Icon.ArrowDownUp)
              .tooltip('Częściowy zwrot zamówienia')
              .command((item) => this.handlePartialRefundClick(item))
              .build(),

            builder
              .initCommand(AdminOrderListItemAction.FullRefund)
              .visibility((item) => item.canBeRefunded)
              .icon(Icon.ArrowDownUp)
              .tooltip('Pełny zwrot zamówienia')
              .command((item) => this.confirmAndFullRefundOrderById(item))
              .build(),


            builder
              .initCommand(AdminOrderListItemAction.Cancel)
              .icon(Icon.XCircle)
              .tooltip('Anuluj')
              .visibility((item) => item.canBeCancelled)
              .command((model) => this.confirmAndCancel(model))
              .build(),
          ])
          .build()
      ])
      .filters((builder) => [
        builder //
          .initText('number')
          .build(),

        builder //
          .initSelect('hasError')
          .dictionary(() => this.booleanTypeSelectItemsProvider.selectItems())
          .build(),

        builder //
          .initMultiSelect('status')
          .dictionary(() => this.orderStatusProvider.selectItems())
          .build(),

        builder //
          .initNumber('totalPrice')
          .build(),

        builder //
          .initText('buyerDignity')
          .build(),

        builder //
          .initSelect('shippingCountries')
          .dictionary(() => this.dictionaryCountrySelectItemsProvider.fetchSelectItems('isoCode'))
          .build(),

        builder //
          .initMultiSelect('shippingMethodIds')
          .dictionary(() => this.shippingMethodSelectItemsProvider.fetchSelectItems('id'))
          .build(),

        builder //
          .initMultiSelect('shippingStatuses')
          .dictionary(() => this.shipmentStatusProvider.selectItems())
          .build(),

        builder //
          .initText('products')
          .build(),

        builder //
          .initText('paymentMethod')
          .build(),

        builder //
          .initDateRange('createdAt')
          .build(),

        builder //
          .initText('variants')
          .build(),

        // builder //
        //   .initSelect('skinType')
        //   .dictionary(() => {
        //     return of(
        //       [
        //         new SelectItem('sucha', 'sucha'),
        //         new SelectItem('tłusta', 'tłusta'),
        //         new SelectItem('mieszana', 'mieszana'),
        //         new SelectItem('normalna', 'normalna'),
        //       ]
        //     );
        //   })
        //   .build(),
      ])
      .columns((builder) => [
        builder //
          .initSelection()
          .build(),

        builder //
          .initCustom('number')
          .width('8rem')
          .label('Numer')
          .sortKey('number')
          .filterKey('number')
          .build(),

        builder //
          .initCustom('hasError')
          .width('10rem')
          .label('Błąd zamówienia')
          .sortKey('hasError')
          .filterKey('hasError')
          .build(),

        builder //
          .initCustom('buyer')
          .width('18rem')
          .label('Dane kupującego')
          .sortKey('buyerDignity')
          .filterKey('buyerDignity')
          .build(),

        builder //
          .initCustom('shippingCountries')
          .width('12rem')
          .label('Kraj dostawy')
          .sortKey('shippingCountries')
          .filterKey('shippingCountries')
          .build(),

        builder //
          .initCustom('products')
          .width('20rem')
          .label('Produkty')
          .sortKey('products')
          .filterKey('products')
          .build(),

        builder //
          .initCustom('paymentMethod')
          .width('12rem')
          .label('Metoda płatności')
          .sortKey('paymentMethod')
          .filterKey('paymentMethod')
          .build(),

        builder //
          .initCustom('status')
          .width('12rem')
          .label('Status')
          .sortKey('status')
          .filterKey('status')
          .build(),

        builder //
          .initCustom('totalPrice')
          .width('10rem')
          .label('Kwota')
          .sortKey('totalPrice')
          .filterKey('totalPrice')
          .build(),

        builder //
          .initCustom('shippingMethods')
          .width('16rem')
          .label('Metoda wysyłki')
          .filterKey('shippingMethodIds')
          .build(),

        builder //
          .initCustom('invoices')
          .width('12rem')
          .label('Faktury')
          .build(),

        builder //
          .initCustom('shipments')
          .width('16rem')
          .label('Wysyłka')
          .filterKey('shippingStatuses')
          .build(),

        builder //
          .initDateTime('createdAt')
          .width('12rem')
          .label('Data zamówienia')
          .sortable(true)
          .sortKey('createdAt')
          .filterable(true)
          .filterKey('createdAt')
          .build(),

        builder //
          .initCustom('variants')
          .width('16rem')
          .label('Ankieta')
          .filterable(true)
          .filterKey('variants')
          .build(),
      ])
      .dataSource({
        fetch: (
          lazyLoad: TableLazyLoad<AdminOrderCollectionFilter>,
        ): Observable<TableDataPayload<AdminOrderCollectionItem>> =>
          this.orderDataSource.fetchCollection(lazyLoad),
      })
      .build();
  }

  public handlePartialRefundSubmitEvent(): void {
    this.closePartialRefundDrawer();
    this.refresh();
  }

  public handlePartialRefundCancelEvent(): void {
    this.closePartialRefundDrawer();
  }

  protected readonly customClosePartialRefundDrawerEvent = () => {
    this.closePartialRefundDrawer();
  };

  private handlePartialRefundClick(item: AdminOrderCollectionItem): void {
    this.partialRefundOrderId.set(item.id);
    this.partialRefundDrawerVisibility.set(true);
  }

  private confirmAndFullRefundOrderById(item: AdminOrderCollectionItem): void {
    this.orderDataSource
      .confirmAndFullRefundOrderById(item.id)
      .pipe(once())
      .subscribe(() => this.refresh());
  }

  private confirmAndCancel(item: AdminOrderCollectionItem): void {
    this.orderDataSource
      .confirmAndCancelById(item.id)
      .pipe(once())
      .subscribe(() => this.refresh());
  }

  private confirmAndMarkAsPaid(item: AdminOrderCollectionItem): void {
    this.orderDataSource
      .confirmAndMarkAsPaidById(item.id)
      .pipe(once())
      .subscribe(() => this.refresh());
  }

  private closePartialRefundDrawer(): void {
    this.partialRefundDrawerVisibility.set(false);
    this.partialRefundOrderId.set(null);
  }

  private refresh(): void {
    this.table.refresh();
    this.refreshPaidOrderCounts();
  }

  private reconfigureTableCollectionActions(): void {
    this.configureTableCollectionActions();
  }

  private getBackgroundTaskProgressLabel(): string {
    const progress = this.backgroundTaskProgress();

    return progress === this.undefinedProgress
      ? 'Trwa generowanie etykiet...'
      : `Trwa generowanie etykiet (${progress}%)`;
  }

  private configureTableCollectionActions(): void {
    this.customCollectionActions.set([
      this.tableCollectionActionBuilder
        .label('Oznacz jako zrealizowane')
        .key(AdminOrderCollectionAction.MarkAsComplete)
        .visibility(() => !this.waitingForMarkAsCompleteTaskReady())
        .command((selection) => this.markAsComplete(selection))
        .build(),

      this.tableCollectionActionBuilder
        .label(this.getBackgroundTaskProgressLabel())
        .visibility(() => this.waitingForMarkAsCompleteTaskReady())
        .key(AdminOrderCollectionAction.MarkAsCompletedPending)
        .disability(() => true)
        .command(() => {
          // Do nothing
        })
        .build(),

      this.tableCollectionActionBuilder
        .label('Pobierz etykiety')
        .key(AdminOrderCollectionAction.DownloadLabels)
        .command((selection) => this.downloadLabels(selection))
        .build(),
    ]);
  }

  private markAsComplete(
    selection: TableRowsSelection<
      AdminOrderCollectionItem,
      AdminOrderCollectionFilter
    >,
  ) {
    const { includedIds } = selection;

    this.orderDataSource
      .markAsCompleteMultiple({
        includedIds: includedIds as Guid[],
      })
      .subscribe((id) => {
        this.waitingForMarkAsCompleteTaskReady.set(true);
        this.showSuccessForMarkAsComplete();
        this.reconfigureTableCollectionActions();
        this.listenToTaskMarkAsCompleteTaskReady(id);
        this.refresh();
      });
  }

  private downloadLabels(
    selection: TableRowsSelection<
      AdminOrderCollectionItem,
      AdminOrderCollectionFilter
    >,
  ) {
    const { includedIds } = selection;

    this.orderDataSource
      .downloadLabels({
        includedIds: includedIds as Guid[],
      })
      .subscribe(() => {
        this.showSuccessForDownloadLabels();
        this.refresh();
      });
  }

  private showSuccessForDownloadLabels(): void {
    this.toast.show((builder) =>
      builder
        .initSuccess(
          'Etykiety dla wybranych rekordów zostały pobrane pomyślnie.',
        )
        .build(),
    );
  }

  private showSuccessForMarkAsComplete(): void {
    this.toast.show((builder) =>
      builder
        .initSuccess(
          'Generowanie etykiet zostało rozpoczęte. Po jego zakończeniu plik powinien zostać automatycznie pobrany. Pozostań na stronie aż do zakończenia procesu.',
        )
        .build(),
    );
  }

  private refreshPaidOrderCounts(): void {
    this.adminOrderStats.refreshCountOfPaidOrders().subscribe();
  }

  private listenToTaskMarkAsCompleteTaskReady(id: Guid): void {
    const poolingTime = 5000;

    timer(0, poolingTime)
      .pipe(
        switchMap(() =>
          this.backgroundTaskDataSource.fetchStatus({ id }),
        ),
        tap((task) => {
          if (task.isDone()) {
            if (isNil(task.taskData)) {
              return;
            }

            window.location.replace(task.taskData);
          } else {
            this.backgroundTaskProgress.set(task.progress);
            this.reconfigureTableCollectionActions();
          }
        }),
        takeWhile((task) => !task?.isDone(), true),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (task) => {
          if (task.isDone()) {
            if (isNil(task.taskData)) {
              this.toast.show((builder) =>
                builder
                  .initWarning('Proces został zakończony. Brak pliku do pobrania ze względu na brak zamówień do przetworzenia.')
                  .build(),
              );
            } else {
              this.toast.show((builder) =>
                builder
                  .initSuccess('Za chwilę nastąpi pobieranie pliku.')
                  .build(),
              );
            }

            this.backgroundTaskProgress.set(this.undefinedProgress);
            this.waitingForMarkAsCompleteTaskReady.set(false);
            this.reconfigureTableCollectionActions();
            this.refresh();
          }
        },
      });
  }

  private shouldVisibleMore(item: AdminOrderCollectionItem): boolean {
    return [item.canBeRefunded, item.canBeCancelled, item.canBeMarkAsPaid].some(
      (value) => value,
    );
  }
}
